import { T, type useGT } from 'gt-react';
import { cn } from '@/lib/utils';
import type { CartAssignment } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import {
  EMPTY_CART_STAT,
  type CartCreatePlan,
  type CartStat,
} from '../utils/assignmentCarts';
import { Chip } from './FilterChip';
import { ReleaseCartButton } from './ReleaseCartButton';
import { ForwardBuyRemoveAction } from './ForwardBuyRemoveAction';

function formatCurrency(v: number) {
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

interface AssignmentCartRailProps {
  supplierId: string;
  railCarts: CartAssignment[];
  /** False in detail mode: the cart header replaces the chips. */
  showChips: boolean;
  cartStats: Map<string, CartStat>;
  selectedShipTo: string | null;
  forwardBoughtShipTos: Set<string>;
  creatingCartKeys: Set<string>;
  planForCart: (shipToWarehouseId: string) => CartCreatePlan;
  onSelectShipTo: (shipTo: string | null) => void;
  onCreateCart: (
    shipToWarehouseId: string,
    summary: CartCreatePlan['summary'],
    releaseSuggestionIds: string[],
  ) => void;
  gt: ReturnType<typeof useGT>;
}

/**
 * MOV / freight rail — one chip per ship-to PO. Carts are live aggregates over
 * the flat rows, not containers: clicking a chip scopes the table to that
 * cart; Create mints its PO/TOs. Below it, forward buying's one action per cart
 * that currently carries an uplift.
 */
export function AssignmentCartRail({
  supplierId,
  railCarts,
  showChips,
  cartStats,
  selectedShipTo,
  forwardBoughtShipTos,
  creatingCartKeys,
  planForCart,
  onSelectShipTo,
  onCreateCart,
  gt,
}: AssignmentCartRailProps) {
  const forwardBoughtCarts = railCarts.filter((cart) =>
    forwardBoughtShipTos.has(cart.shipToWarehouseId),
  );
  return (
    <>
      {showChips && railCarts.length > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {railCarts.length > 1 && (
            <Chip
              active={selectedShipTo == null}
              onClick={() => onSelectShipTo(null)}
            >
              <T context="assignment board rail all carts">All carts</T>
            </Chip>
          )}
          {railCarts.map((cart) => {
            const { summary, releaseSuggestionIds } = planForCart(
              cart.shipToWarehouseId,
            );
            return (
              <MovCartChip
                key={cart.shipToWarehouseId}
                cart={cart}
                stats={cartStats.get(cart.shipToWarehouseId) ?? EMPTY_CART_STAT}
                selected={selectedShipTo === cart.shipToWarehouseId}
                creating={creatingCartKeys.has(
                  `${supplierId}:${cart.shipToWarehouseId}`,
                )}
                releaseCount={releaseSuggestionIds.length}
                onSelect={() =>
                  onSelectShipTo(
                    selectedShipTo === cart.shipToWarehouseId
                      ? null
                      : cart.shipToWarehouseId,
                  )
                }
                onCreate={() =>
                  onCreateCart(
                    cart.shipToWarehouseId,
                    summary,
                    releaseSuggestionIds,
                  )
                }
                gt={gt}
              />
            );
          })}
        </div>
      )}
      {forwardBoughtCarts.length > 0 && (
        <div className="mb-2 flex flex-col gap-1">
          {forwardBoughtCarts.map((cart) => (
            <ForwardBuyRemoveAction
              key={cart.shipToWarehouseId}
              supplierId={supplierId}
              shipToWarehouseId={cart.shipToWarehouseId}
              shipToWarehouseName={
                railCarts.length > 1 ? cart.shipToWarehouseName : null
              }
            />
          ))}
        </div>
      )}
    </>
  );
}

type Gt = ReturnType<typeof useGT>;

function chipToneClass(selected: boolean): string {
  if (selected) return 'border-blue-500 bg-blue-50 ring-1 ring-blue-200';
  return 'border-gray-200 bg-white hover:border-gray-300';
}

function MovCartChip({
  cart,
  stats,
  selected,
  creating,
  releaseCount,
  onSelect,
  onCreate,
  gt,
}: {
  cart: CartAssignment;
  stats: CartStat;
  selected: boolean;
  creating: boolean;
  releaseCount: number;
  onSelect: () => void;
  onCreate: () => void;
  gt: Gt;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-lg border px-2.5 py-1.5 transition-colors',
        chipToneClass(selected),
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className="flex items-center gap-2 text-left"
        title={gt('Filter the table to this ship-to PO', {
          context: 'mov rail chip select tooltip',
        })}
      >
        <span
          className={cn(
            'rounded px-1.5 py-0.5 text-[10px] font-medium leading-none shrink-0',
            cart.shipToWarehouseType === 'hub'
              ? 'bg-indigo-100 text-indigo-700'
              : 'bg-gray-200 text-gray-600',
          )}
        >
          {cart.shipToWarehouseType ?? 'WH'}
        </span>
        <span className="text-xs font-medium truncate max-w-[10rem]">
          {cart.shipToWarehouseName}
        </span>
        <span className="text-xs font-medium text-green-700 tabular-nums shrink-0">
          {formatCurrency(stats.poValue)}
        </span>
        {stats.soCount > 0 && (
          <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700 shrink-0">
            {stats.soCount} <T context="mov rail chip so badge">SO</T>
          </span>
        )}
      </button>
      <ReleaseCartButton
        creating={creating}
        releaseCount={releaseCount}
        onCreate={onCreate}
      />
    </div>
  );
}
